import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import Select from "react-select";
import { useForm, useWatch, Controller } from "react-hook-form";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faCheck,
  faPrint,
  faEnvelope,
  faTrash,
  faCheckSquare,
  faSquare,
  faExclamationTriangle,
  faBuilding,
  faUser,
  faChild,
  faCalendarAlt,
  faStethoscope,
  faShieldAlt,
  faClock,
  faVenusMars,
  faFileMedical,
  faExclamationCircle,
  faBan,
  faSearch,
  faClockRotateLeft, // For expired/used
} from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Swal from "sweetalert2";
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useFilters,
} from "react-table";
import { BASE_URL } from "../../config"; // Assume

const APE_DATEPICKER_PORTAL_ID = "ape-scheduler-datepicker-portal";
const APE_DATEPICKER_POPPER_CLASS = "ape-scheduler-datepicker-popper";
const APE_DATEPICKER_PORTAL_STYLE = `
  #${APE_DATEPICKER_PORTAL_ID} .${APE_DATEPICKER_POPPER_CLASS} {
    z-index: 10000 !important;
  }
`;

const ApeSchedulerPage = () => {
  const [selectedCorp, setSelectedCorp] = useState(null);
  const [tableData, setTableData] = useState({ data: [], pagination: {} });
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [selectAll, setSelectAll] = useState(false);
  const {
    handleSubmit,
    reset,
    control,
    setValue,
    register,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      corporate: null,
      plan: null,
      memberType: "All",
      gender: "All",
      minAge: 1,
      maxAge: 120,
      scheduleType: "S1", // Default
      package: null,
      provider: null,
      remarks: "",
      amount: 0,
      members: [], // Array of selected member objects
    },
  });
  const corporate = useWatch({ control, name: "corporate" });
  const plan = useWatch({ control, name: "plan" });
  const memberType = useWatch({ control, name: "memberType" });
  const gender = useWatch({ control, name: "gender" });
  const minAge = useWatch({ control, name: "minAge" });
  const maxAge = useWatch({ control, name: "maxAge" });
  const provider = useWatch({ control, name: "provider" });
  const selectedMembers = watch("members"); // For select all logic
  const [corporates, setCorporates] = useState([]);
  const [plans, setPlans] = useState([{ value: "All", label: "All" }]);
  const [packages, setPackages] = useState([]);
  const [providers, setProviders] = useState([]); // single selection lang ang Providers
  const [membersList, setMembersList] = useState([]); // Raw list for checkboxes
  const [memberSearch, setMemberSearch] = useState("");
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("authentication/token")}`,
  });

  // Load Dropdowns on Mount
  useEffect(() => {
    loadCorporates();
    loadPackages();
    loadProviders();
  }, []);

  const loadCorporates = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/ape-scheduler/corporates`, {
        headers: getAuthHeaders(),
      });
      setCorporates(res.data.data);
    } catch (err) {
      Swal.fire("Error", "Failed to load corporates", "error");
    }
  };
  const loadPackages = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/ape-scheduler/packages`, {
        headers: getAuthHeaders(),
      });
      setPackages(res.data.data);
    } catch (err) {
      Swal.fire("Error", "Failed to load packages", "error");
    }
  };
  const loadProviders = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/ape-scheduler/providers`, {
        headers: getAuthHeaders(),
      });
      setProviders(res.data.data);
    } catch (err) {
      Swal.fire("Error", "Failed to load providers", "error");
    }
  };

  // Cascade Plans
  useEffect(() => {
    if (corporate?.value && !isResetting) {
      setIsResetting(true);
      setFormLoading(true);
      axios
        .get(`${BASE_URL}/ape-scheduler/plans/${corporate.value}`, {
          headers: getAuthHeaders(),
        })
        .then((res) => {
          setPlans(res.data.data);
          setValue("plan", null);
        })
        .finally(() => {
          setFormLoading(false);
          setIsResetting(false);
        });
    }
    // }, [corporate, setValue, isResetting]);
  }, [corporate]);

  // Cascade Members List
  useEffect(() => {
    if (corporate?.value && !isResetting) {
      setFormLoading(true);
      // Reset selections when list refreshes
      setSelectAll(false);
      setValue("members", []);
      const params = new URLSearchParams({
        plan_code: plan?.value || "All",
        member_type: memberType,
        gender: gender,
        min_age: minAge || 0,
        max_age: maxAge || 150,
        coverage_code: "APE", // Always append for backend flags
      });
      // Append scheduled check params only if provider and dates are selected
      if (provider?.value && startDate && endDate) {
        const dateFrom = startDate.toISOString().split("T")[0];
        const dateTo = endDate.toISOString().split("T")[0];
        params.append("provider_code", provider.value);
        params.append("date_from", dateFrom);
        params.append("date_to", dateTo);
      }
      axios
        .get(`${BASE_URL}/ape-scheduler/members/${corporate.value}?${params}`, {
          headers: getAuthHeaders(),
        })
        .then((res) => {
          setMembersList(res.data.data); // Set raw list for checkboxes
        })
        .finally(() => setFormLoading(false));
    }
  }, [corporate, plan, memberType, gender, minAge, maxAge, provider, startDate, endDate, setValue]);

  // Sync Select All state with selections (now considering only selectable members based on all flags)
  useEffect(() => {
    if (membersList.length > 0) {
      const availableMembers = membersList.filter((m) => isMemberSelectable(m));
      const allAvailableSelected =
        availableMembers.length > 0 &&
        availableMembers.every((m) =>
          selectedMembers.some((s) => s.value === m.value)
        );
      setSelectAll(allAvailableSelected);
    }
  }, [selectedMembers, membersList]);

  // Helper: Check if member is selectable (all critical flags true)
  const isMemberSelectable = (member) => {
    return (
      member.is_covered &&
      member.is_corporate_active &&
      member.is_corporate_within_period &&
      member.is_member_active &&
      member.is_within_age_limit &&
      member.is_plan_active &&
      member.has_provider_access &&
      member.is_coverage_active &&
      member.has_no_pending_admission &&
      member.is_maternity_eligible &&
      !member.is_ape_used &&
      // member.is_ape_eligible &&
      member.is_provider_type_allowed &&
      member.is_provider_accredited &&
      !member.is_scheduled
    );
  };

  // Helper: Get reasons why not selectable (for badges/tooltips)
  const getNonSelectableReasons = (member) => {
    const reasons = [];
    if (!member.is_covered) reasons.push("APE not covered");
    if (!member.is_corporate_active) reasons.push("Corporate inactive");
    if (!member.is_corporate_within_period) reasons.push("Corporate expired");
    // if (!member.is_corporate_special) reasons.push("Corporate special status");
    if (!member.is_member_active) reasons.push("Member inactive");
    if (!member.is_within_age_limit) reasons.push("Age limit exceeded");
    if (!member.is_plan_active) reasons.push("Plan inactive");
    if (!member.has_provider_access) reasons.push("No provider access");
    if (!member.is_coverage_active) reasons.push("Coverage inactive");
    if (!member.has_no_pending_admission) reasons.push("Pending admission");
    if (!member.is_maternity_eligible) reasons.push("Not maternity eligible");

    if (member.is_ape_used) reasons.push("APE already used");

    // if (!member.is_ape_eligible) reasons.push("APE ineligible (schedule/trans)");

    if (!member.is_provider_type_allowed) reasons.push("Provider type not allowed");
    if (!member.is_provider_accredited) reasons.push("Provider not accredited");

    if (member.is_scheduled) reasons.push("Already scheduled");
    return reasons;
  };

  const filteredMembersList = useMemo(() => {
    const keywords = memberSearch.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (keywords.length === 0) return membersList;

    return membersList.filter((member) => {
      const searchableText = [
        member.member_id,
        member.value,
        member.first_name,
        member.last_name,
        member.fullname,
        member.label,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return keywords.every((keyword) => searchableText.includes(keyword));
    });
  }, [membersList, memberSearch]);

  // Select All Handler (now only selects available members)
  const handleSelectAllChange = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const available = membersList.filter(isMemberSelectable);
      setValue("members", available);
    } else {
      setValue("members", []);
    }
  };

  // Load Schedules Table
  useEffect(() => {
    if (corporate?.value) {
      setLoading(true);
      const params = new URLSearchParams({
        per_page: 10,
        page: 1,
        status_flag: "final", // Default
      });
      axios
        .get(
          `${BASE_URL}/ape-scheduler/schedules/${corporate.value}?${params}`,
          { headers: getAuthHeaders() }
        )
        .then((res) => {
          setTableData({
            data: res.data.data,
            pagination: res.data.pagination,
          });
        })
        .finally(() => setLoading(false));
    }
  }, [corporate]);

  const onSubmitDraft = (data) => {
    setFormLoading(true);
    const submitData = {
      ...data,
      corp_code: corporate?.value,
      date_from: startDate.toISOString().split("T")[0],
      date_to: endDate.toISOString().split("T")[0],
      member_type: memberType,
      members: data.members.map((m) => m.value), // Extract values
    };
    axios
      .post(`${BASE_URL}/ape-scheduler/drafts`, submitData, {
        headers: getAuthHeaders(),
      })
      .then(() => Swal.fire("Success", "Draft saved!", "success"))
      .finally(() => setFormLoading(false));
  };
  const onSubmitFinalize = (data) => {
    Swal.fire({
      title: "Finalize Schedule?",
      text: "This will create final entries and clear draft.",
      icon: "warning",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setFormLoading(true);
        const submitData = {
          ...data,
          date_from: startDate.toISOString().split("T")[0],
          date_to: endDate.toISOString().split("T")[0],
          members: data.members.map((m) => m.value),
          member_type: memberType,
          status_flag: "final",
        };
        axios
          .post(`${BASE_URL}/ape-scheduler/schedules/finalize`, submitData, {
            headers: getAuthHeaders(),
          })
          .then(() => {
            Swal.fire("Success", "Schedule finalized!", "success");
            reset();
          })
          .finally(() => setFormLoading(false));
      }
    });
  };
  const onSubmitSetup = async (data) => {
    const confirmResult = await Swal.fire({
      title: "Set-up Schedule?",
      text: "This will check for duplicates and add final entries.",
      icon: "warning",
      showCancelButton: true,
    });

    if (confirmResult.isConfirmed) {
      const loadingSwal = Swal.fire({
        title: "Please wait...",
        text: "While setting up your new APE Schedule",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      try {
        const submitData = {
          ...data,
          corp_code: corporate?.value,
          member_type: memberType,
          members: data.members.map((m) => m.value),
          coverage_code: "APE", // Default
          client_code: selectedCorp?.label.split("[")[1]?.split("]")[0] || null, // Extract from label if needed
          gender: data?.gender || null,
          min_age: data?.minAge || 0,
          max_age: data?.maxAge || 150,
          ape_package: data?.package?.value || null,
          remarks: data?.remarks || "",
          amount: data?.amount || 0,
          plan_code: data?.plan?.value || "All",
          provider_code: data?.provider?.value || "All",
          start_date: startDate.toISOString().split("T")[0],
          end_date: endDate.toISOString().split("T")[0],
          date_from: startDate.toISOString().split("T")[0],
          date_to: endDate.toISOString().split("T")[0],
          package_code: data?.package?.value || null,
          schedule_type: data?.scheduleType || null,
        };
        const response = await axios.post(`${BASE_URL}/ape-scheduler/setups?action=final`, submitData, {
          headers: getAuthHeaders(),
        });

        Swal.close(); // Close loading modal
        if (response.data.status === 'success') {
          Swal.fire({
            title: "Success!",
            text: response.data.message || "Schedule set up successfully!",
            icon: "success",
            confirmButtonText: "OK",
          });
          reset();
        } else {
          throw new Error(response.data.message || "Unknown error occurred");
        }
      } catch (error) {
        Swal.close(); // Close loading modal
        let errorMessage = "An unexpected error occurred";
        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const data = error.response.data;
          if (status === 422) {
            // Validation errors
            errorMessage = data.message ? (typeof data.message === 'object' ? Object.values(data.message)[0][0] : data.message) : "Validation failed";
          } else if (status === 409) {
            errorMessage = data.message || "Duplicate entry detected";
          } else if (status === 401) {
            errorMessage = "Unauthorized access";
          } else if (status === 404) {
            errorMessage = "Resource not found";
          } else if (status === 500) {
            errorMessage = "Server error";
          } else {
            errorMessage = data.message || `Error ${status}`;
          }
        } else if (error.request) {
          // Network error
          errorMessage = "Network error. Please check your connection.";
        } else {
          errorMessage = error.message;
        }
        Swal.fire({
          title: "Error!",
          text: errorMessage,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    }
  };
  const loadSchedules = (corpCode, page = 1) => {
    // Similar to useEffect, with page param
  };
  const handleDelete = (tblId, memberName) => {
    Swal.fire({
      title: "Delete Schedule?",
      text: `For member ${memberName}?`,
      icon: "warning",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(`${BASE_URL}/ape-scheduler/schedules/${tblId}`, {
            headers: getAuthHeaders(),
          })
          .then(() => {
            Swal.fire("Deleted!", "Schedule removed.", "success");
            loadSchedules(corporate.value);
          });
      }
    });
  };
  const handleSendEmail = (recipient, corpCode) => {
    const text =
      recipient === "corporate"
        ? "to Corporate"
        : recipient === "provider"
        ? "to Provider"
        : "to Both";
    Swal.fire({
      title: `Send Email ${text}?`,
      icon: "info",
      showCancelButton: true,
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .post(
            `${BASE_URL}/ape-scheduler/send-email/${recipient}`,
            { corp_code: corpCode },
            { headers: getAuthHeaders() }
          )
          .then(() =>
            Swal.fire("Sent!", "Email sent successfully.", "success")
          );
      }
    });
  };
  const handlePrint = () => {
    // Use window.print() or client-side PDF lib; or call API
    window.print();
  };

  return (
    <div className="p-6 max-w-7xl bg-gray-50 min-h-screen">
      <style>{APE_DATEPICKER_PORTAL_STYLE}</style>
      {/* Form Section */}
      <form
        onSubmit={handleSubmit(onSubmitDraft)}
        className="bg-white p-6 rounded-xl shadow-lg mb-6 transition-shadow duration-300 hover:shadow-xl"
      >
        <h2 className="text-2xl font-bold mb-6 text-gray-800 border-b pb-2">
          📝 Set Up New APE Schedule
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel: Fields */}
          <div className="space-y-4">
            <div className="relative">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Select Account *
              </label>
              <Controller
                name="corporate"
                control={control}
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={corporates}
                    onChange={(opt) => {
                      field.onChange(opt);
                      setSelectedCorp(opt);
                    }}
                    isLoading={formLoading}
                    classNamePrefix="react-select"
                    className="basic-select"
                    placeholder="Select Corporate"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: errors.corporate ? "#ef4444" : "#d1d5db",
                        "&:hover": {
                          borderColor: errors.corporate ? "#ef4444" : "#9ca3af",
                        },
                      }),
                    }}
                  />
                )}
              />
              {errors.corporate && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <FontAwesomeIcon icon={faTrash} className="mr-1" />
                  {errors.corporate.message}
                </p>
              )}
            </div>
            <div className="relative">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Select Plan *
              </label>
              <Controller
                name="plan"
                control={control}
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={plans}
                    isLoading={formLoading}
                    classNamePrefix="react-select"
                    className="basic-select"
                    placeholder="Select Plan"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: errors.plan ? "#ef4444" : "#d1d5db",
                        "&:hover": {
                          borderColor: errors.plan ? "#ef4444" : "#9ca3af",
                        },
                      }),
                    }}
                  />
                )}
              />
              {errors.plan && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <FontAwesomeIcon icon={faTrash} className="mr-1" />
                  {errors.plan.message}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Member Type *
                </label>
                <select
                  {...register("memberType", { required: true })}
                  className={`form-select w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.memberType
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                >
                  <option>All</option>
                  <option>Principal</option>
                  <option>Dependent</option>
                </select>
                {errors.memberType && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                    Required
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Gender *
                </label>
                <select
                  {...register("gender", { required: true })}
                  className={`form-select w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.gender
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                >
                  <option>All</option>
                  <option>Male</option>
                  <option>Female</option>
                </select>
                {errors.gender && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                    Required
                  </p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Min Age *
                </label>
                <input
                  type="number"
                  {...register("minAge", { required: true, min: 0 })}
                  className={`form-input w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.minAge
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {errors.minAge && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                    Required (min 0)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Max Age *
                </label>
                <input
                  type="number"
                  {...register("maxAge", { required: true, min: 0 })}
                  className={`form-input w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.maxAge
                      ? "border-red-300 focus:ring-red-500"
                      : "border-gray-300 focus:ring-blue-500"
                  }`}
                />
                {errors.maxAge && (
                  <p className="text-red-500 text-xs mt-1 flex items-center">
                    <FontAwesomeIcon icon={faTrash} className="mr-1" />
                    Required (min 0)
                  </p>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Schedule Type *
              </label>
              <select
                {...register("scheduleType", { required: true })}
                className="form-select w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
              >
                <option value="S1">Single Day</option>
                <option value="S2">Two Days</option>
                {/* Add more from old JS if needed */}
              </select>
            </div>
            <div className="relative">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Select APE Package *
              </label>
              <Controller
                name="package"
                control={control}
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={packages}
                    isLoading={formLoading}
                    classNamePrefix="react-select"
                    className="basic-select"
                    placeholder="Select Package"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: errors.package ? "#ef4444" : "#d1d5db",
                        "&:hover": {
                          borderColor: errors.package ? "#ef4444" : "#9ca3af",
                        },
                      }),
                    }}
                  />
                )}
              />
              {errors.package && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <FontAwesomeIcon icon={faTrash} className="mr-1" />
                  {errors.package.message}
                </p>
              )}
            </div>
            <div className="relative">
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Search Provider *
              </label>
              <Controller
                name="provider"
                control={control}
                rules={{ required: "Required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={providers}
                    isLoading={formLoading}
                    classNamePrefix="react-select"
                    className="basic-select"
                    placeholder="Select Provider"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        borderColor: errors.provider ? "#ef4444" : "#d1d5db",
                        "&:hover": {
                          borderColor: errors.provider ? "#ef4444" : "#9ca3af",
                        },
                      }),
                    }}
                  />
                )}
              />
              {errors.provider && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <FontAwesomeIcon icon={faTrash} className="mr-1" />
                  {errors.provider.message}
                </p>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Date From *
                </label>
                <DatePicker
                  selected={startDate}
                  onChange={setStartDate}
                  className="form-input w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
                  wrapperClassName="w-full"
                  portalId={APE_DATEPICKER_PORTAL_ID}
                  popperClassName={APE_DATEPICKER_POPPER_CLASS}
                  popperPlacement="bottom-start"
                  popperProps={{ strategy: "fixed" }}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select date"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">
                  Date To *
                </label>
                <DatePicker
                  selected={endDate}
                  onChange={setEndDate}
                  className="form-input w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
                  wrapperClassName="w-full"
                  portalId={APE_DATEPICKER_PORTAL_ID}
                  popperClassName={APE_DATEPICKER_POPPER_CLASS}
                  popperPlacement="bottom-start"
                  popperProps={{ strategy: "fixed" }}
                  dateFormat="yyyy-MM-dd"
                  placeholderText="Select date"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Remarks
              </label>
              <textarea
                {...register("remarks")}
                className="form-textarea w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent border-gray-300"
                rows={3}
                placeholder="Enter remarks..."
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Amount *
              </label>
              <input
                type="number"
                step="0.01"
                {...register("amount", { required: true, min: 0 })}
                className={`form-input w-full p-3 border rounded-lg transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.amount
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-blue-500"
                }`}
                placeholder="0.00"
              />
              {errors.amount && (
                <p className="text-red-500 text-xs mt-1 flex items-center">
                  <FontAwesomeIcon icon={faTrash} className="mr-1" />
                  Required (min 0)
                </p>
              )}
            </div>
          </div>
          {/* Right Panel: Scrollable Member Checkboxes */}
          <div className="space-y-4">
            <label className="block text-sm font-semibold mb-2 text-gray-700">
              Select Members * (Scrollable List)
            </label>
            <div className="flex justify-between items-center mb-3 bg-gray-100 p-2 rounded-lg">
              <label className="flex items-center cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors duration-200">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAllChange}
                  className="mr-2 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                />
                <FontAwesomeIcon
                  icon={selectAll ? faCheckSquare : faSquare}
                  className="mr-2 text-gray-500"
                />
                {selectAll ? "Deselect All" : "Select All"}
              </label>
              <span className="text-sm font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                {selectedMembers.length} selected
              </span>
            </div>
            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
              <label
                htmlFor="member-search"
                className="block text-xs font-semibold text-gray-600 mb-2"
              >
                Search members
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <div className="relative flex-1">
                  <FontAwesomeIcon
                    icon={faSearch}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"
                  />
                  <input
                    id="member-search"
                    type="text"
                    value={memberSearch}
                    onChange={(e) => setMemberSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm transition-colors duration-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Search by first name, last name, or member ID"
                    disabled={formLoading || membersList.length === 0}
                  />
                </div>
                {memberSearch && (
                  <button
                    type="button"
                    onClick={() => setMemberSearch("")}
                    className="px-3 py-2 text-sm font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                  >
                    Clear
                  </button>
                )}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Showing {filteredMembersList.length} of {membersList.length} members
              </p>
            </div>
            {/* <div className="border-2 border-gray-200 rounded-lg h-96 overflow-y-auto p-3 bg-white shadow-inner"> */}
            <div className="border-2 border-gray-200 rounded-lg h-[30rem] overflow-y-auto p-3 bg-white shadow-inner">
              {formLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-500">Loading members...</span>
                </div>
              ) : membersList.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FontAwesomeIcon icon={faSquare} className="text-4xl mb-2" />
                  <p>No members found. Adjust filters above.</p>
                </div>
              ) : filteredMembersList.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FontAwesomeIcon icon={faSearch} className="text-4xl mb-2" />
                  <p>No matching members found.</p>
                  <p className="text-xs mt-1">
                    Try a different first name, last name, or member ID.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredMembersList.map((member, index) => {
                    const isSelected = selectedMembers.some(
                      (m) => m.value === member.value
                    );
                    const isSelectable = isMemberSelectable(member);
                    const reasons = getNonSelectableReasons(member);
                    const tooltipText = reasons.length > 0 ? reasons.join(", ") : "";
                    return (
                      <label
                        key={member.value}
                        className={`flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 hover:bg-blue-50 flex-wrap ${
                          isSelected
                            ? "bg-blue-50 border-2 border-blue-200"
                            : !isSelectable
                            ? "opacity-60 cursor-not-allowed bg-gray-100"
                            : ""
                        }`}
                        title={tooltipText} // Hover tooltip for all reasons
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!isSelectable}
                          onChange={(e) => {
                            if (!isSelectable) return;
                            const checked = e.target.checked;
                            const current = watch("members");
                            if (checked) {
                              setValue("members", [...current, member]);
                            } else {
                              setValue(
                                "members",
                                current.filter((m) => m.value !== member.value)
                              );
                            }
                          }}
                          className="mr-3 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 disabled:opacity-50"
                        />
                        <span className={`text-sm font-medium flex-1 ${!isSelectable ? 'text-gray-400' : 'text-gray-700'}`}>
                          {member.label}
                        </span>
                        {/* Multiple Badges for Reasons */}
                        <div className="flex flex-wrap gap-1 ml-2">
                          {reasons.map((reason, rIndex) => (
                            <span
                              key={rIndex}
                              className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-700 border border-red-200 flex items-center whitespace-nowrap"
                            >
                              <FontAwesomeIcon 
                                icon={
                                  reason.includes("Corporate") ? faBuilding :
                                  reason.includes("Member") ? faUser :
                                  reason.includes("Age") ? faChild :
                                  reason.includes("Plan") ? faCalendarAlt :
                                  reason.includes("Provider access") ? faStethoscope :
                                  reason.includes("Coverage") ? faShieldAlt :
                                  reason.includes("Pending") ? faClock :
                                  reason.includes("Maternity") ? faVenusMars :
                                  reason.includes("APE") ? faFileMedical :
                                  reason.includes("Type") ? faExclamationCircle :
                                  reason.includes("Accredited") ? faBan :
                                  reason.includes("Scheduled") ? faExclamationTriangle : faExclamationTriangle
                                } 
                                className="mr-1 text-xs" 
                              />
                              {reason.split(" ")[0]} {/* Shorten for space */}
                            </span>
                          ))}
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            {errors.members && (
              <p className="text-red-500 text-xs mt-1 flex items-center bg-red-50 p-2 rounded">
                <FontAwesomeIcon icon={faTrash} className="mr-1" />
                Select at least one member
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 mt-6 p-4 border-t bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={handleSubmit(onSubmitSetup)}
            className="btn bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            disabled={formLoading || selectedMembers.length === 0}
          >
            <FontAwesomeIcon icon={faCheck} className="mr-2" />
            Set-up Schedule
          </button>
        </div>
      </form>
    </div>
  );
};
export default ApeSchedulerPage;
